import type { useI18nContext } from "@procore/core-react";
import { object, date, number, string, array } from "yup";
import { getBaseSchemaForRequestAndAssignment } from "../validation-schema";
import type { AssignmentFormValues } from "./types";
import { getTotalHours } from "../helper";

export const assignmentSchema = (
   I18n: ReturnType<typeof useI18nContext>,
   includeStatus: boolean,
   selectedWorkDays: { [key: string]: boolean },
   assignmentByAllocation: boolean,
   assignmentByTime: boolean,
   isProcoreIdAvailable: boolean,
   isEquipmentEnabled: boolean,
   isOverTimeSelected: boolean,
   isCreateFlow: boolean,
   projectStartDate?: Date | null,
   projectEndDate?: Date | null,
) => {
   const baseSchema = getBaseSchemaForRequestAndAssignment(
      I18n,
      selectedWorkDays,
      assignmentByAllocation,
      assignmentByTime,
      includeStatus,
      projectStartDate,
      projectEndDate,
   );

   const getResourceValidationString = () => {
      // this logic will be removed once we complete the equipment beta
      if (isEquipmentEnabled) {
         return I18n.t(
            "views.company.workforce_planning.assignment.validations.labor_or_equipment_required",
         );
      }
      return I18n.t("views.company.workforce_planning.validations.required_field");
   };

   const isPopulated = (value: unknown): boolean =>
      Array.isArray(value) ? value.length > 0 : !!value;

   return object().shape({
      resource: isCreateFlow
         ? array(object({ id: string(), label: string() }))
              .nullable()
              .test("resource-or-equipment", getResourceValidationString(), function (value) {
                 return (
                    (Array.isArray(value) && value.length > 0) ||
                    isPopulated(this.parent?.equipment)
                 );
              })
         : object({ id: string(), label: string() })
              .nullable()
              .test("resource-or-equipment", getResourceValidationString(), function (value) {
                 return !!value || isPopulated(this.parent?.equipment);
              }),

      equipment: isCreateFlow
         ? array(object({ id: string(), name: string() }))
              .nullable()
              .test(
                 "equipment-or-resource",
                 I18n.t(
                    "views.company.workforce_planning.assignment.validations.labor_or_equipment_required",
                 ),
                 function (value) {
                    if (isProcoreIdAvailable) {
                       return (
                          (Array.isArray(value) && value.length > 0) ||
                          isPopulated(this.parent?.resource)
                       );
                    }
                    return true;
                 },
              )
         : object({ id: string(), name: string().nullable() })
              .nullable()
              .test(
                 "equipment-or-resource",
                 I18n.t(
                    "views.company.workforce_planning.assignment.validations.labor_or_equipment_required",
                 ),
                 function (value) {
                    if (isProcoreIdAvailable) {
                       return !!value || isPopulated(this.parent?.resource);
                    }
                    return true;
                 },
              ),

      ...baseSchema,

      ...(isOverTimeSelected && {
         pay_split: object()
            .shape({
               overtime: number().nullable(),
               straight: number().nullable(),
               unpaid: number().nullable(),
            })
            .test("sum-equals-total-hours", function (pay_split) {
               const formValues: AssignmentFormValues = this.parent;
               const totalHours = getTotalHours(formValues.end_time.id, formValues.start_time.id);
               const validationMessage = I18n.t(
                  "views.company.workforce_planning.assignment.validations.total_time_add_check",
                  { hours: totalHours },
               );

               const overtime = pay_split?.overtime ?? 0;
               const straight = pay_split?.straight ?? 0;
               const unpaid = pay_split?.unpaid ?? 0;

               const sum = overtime + straight + unpaid;
               return sum === totalHours || this.createError({ message: validationMessage });
            }),
      }),
   });
};

export const assignmentAlertsSchema = (
   I18n: ReturnType<typeof useI18nContext>,
   shouldScheduleDelivery: boolean,
) => {
   if (!shouldScheduleDelivery) return;

   return object().shape({
      scheduled_day: date().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
      hour: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
      minute: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
      day_night: object().required(
         I18n.t("views.company.workforce_planning.validations.required_field"),
      ),
   });
};
